/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: AI识别记录列表管理Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-19 19:16:28
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { App } from 'antd'
import { Dayjs } from 'dayjs'
import {
  fetchAIRecognitionList,
  exportAIRecognitionExcel
} from '../../../actions/aiRecognition'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { RecognitionRecord } from '../types'
import { useSensorTracking } from './useSensorTracking'
import { usePollAndDownload } from './usePollAndDownload'

// 分页大小常量
const DEFAULT_PAGE_SIZE = 10

interface UseExportListOptions {
  initialPageSize?: number
}

interface UseExportListReturn {
  // 状态
  recognitionRecords: RecognitionRecord[]
  currentPage: number
  currentPageSize: number
  totalRecords: number
  loading: boolean
  dateRange: [Dayjs | null, Dayjs | null] | null

  // 事件处理函数
  handleFetchRecords: (page?: number, size?: number) => Promise<void>
  handlePageChange: (page: number) => void
  handlePageSizeChange: (current: number, size: number) => void
  handleDownloadExcel: () => Promise<void>
  setDateRange: (range: [Dayjs | null, Dayjs | null] | null) => void
}

/**
 * AI识别记录列表管理Hook
 * 封装了记录获取、分页处理、Excel导出等功能
 */
export const useExportList = (
  options: UseExportListOptions = {}
): UseExportListReturn => {
  const { message } = App.useApp()
  const { initialPageSize = DEFAULT_PAGE_SIZE } = options
  const dispatch = useAppDispatch()
  const { reportAISafetyHazardsHistoryClick } = useSensorTracking()

  // 从Redux store获取用户和团队信息
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const groupId = useAppSelector((state) => state.group.groupId)

  // 状态管理
  const [recognitionRecords, setRecognitionRecords] = useState<
    RecognitionRecord[]
  >([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    initialPageSize
  )
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null)

  /**
   * 获取识别记录数据
   */
  const handleFetchRecords = useCallback(
    async (page = 1, size = currentPageSize) => {
      setLoading(true)

      // 构建请求参数
      const params: any = {
        groupID: groupId,
        pageNo: page,
        pageSize: size
      }

      // 如果有时间范围，添加时间参数
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startTime = dateRange[0].valueOf()
        params.endTime = dateRange[1].valueOf()
      }

      try {
        const result = await dispatch(fetchAIRecognitionList(params))

        setRecognitionRecords(result.list || [])
        setTotalRecords(result.total || 0)
        setCurrentPage(page)
        setLoading(false)
      } catch (error) {
        console.error('获取识别记录失败:', error)
        message.error('获取识别记录失败')
        setLoading(false)
      }
    },
    [dispatch, groupId, dateRange, currentPageSize]
  )

  /**
   * 处理页码变化
   */
  const handlePageChange = useCallback(
    (page: number) => {
      handleFetchRecords(page, currentPageSize)
    },
    [handleFetchRecords, currentPageSize]
  )

  /**
   * 处理页面大小变化
   */
  const handlePageSizeChange = useCallback(
    (current: number, size: number) => {
      setCurrentPageSize(size)
      setCurrentPage(1)
      handleFetchRecords(1, size)
    },
    [handleFetchRecords]
  )

  const { pollAndDownload } = usePollAndDownload()

  /**
   * 处理下载Excel
   */
  const handleDownloadExcel = useCallback(async () => {
    if (recognitionRecords.length === 0) {
      message.warning('暂无数据可导出')
      return
    }

    // 上报AI识别安全隐患记录页埋点
    reportAISafetyHazardsHistoryClick({
      clickItem: 'downloadExcel',
      historyRecordNum: totalRecords
    })

    try {
      const exportParams = {
        groupID: groupId,
        userID: userId || '',
        format: 'excel',
        ids: recognitionRecords.map((record) => record.id)
      }

      const result = (await dispatch(
        exportAIRecognitionExcel(exportParams)
      )) as any

      if (result.exportURL) {
        message.success('正在生成文件，请稍候...')

        // 使用通用的 download 方法
        pollAndDownload({
          exportURL: result.exportURL,
          fileName: result.fileName || 'recognition_records_list.xlsx',
          defaultFileName: 'recognition_records_list.xlsx'
        })
          .then(() => {
            message.success('文件下载成功')
          })
          .catch((error) => {
            console.error('下载失败:', error)
            message.error('下载失败，请稍后重试')
          })
      } else {
        throw new Error('导出失败')
      }
    } catch (error) {
      console.error('Excel导出错误:', error)
      message.error('导出失败，请重试')
    }
  }, [
    dispatch,
    recognitionRecords,
    groupId,
    userId,
    totalRecords,
    pollAndDownload
  ])

  // 组件挂载时获取数据
  useEffect(() => {
    handleFetchRecords()
  }, [handleFetchRecords])

  return {
    // 状态
    recognitionRecords,
    currentPage,
    currentPageSize,
    totalRecords,
    loading,
    dateRange,

    // 事件处理函数
    handleFetchRecords,
    handlePageChange,
    handlePageSizeChange,
    handleDownloadExcel,
    setDateRange
  }
}

export default useExportList
